import XlsxCellViewer from './XlsxCellViewer';
import type { XlsxAnchor } from '../../../types/xlsxAnchor';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import PDFHighlightViewer from '../PDFHighlightViewer';
import { isFileNotFoundError } from './previewErrors';
import {
  PREVIEWABLE_IMAGE_TYPES,
  PREVIEWABLE_SPREADSHEET_TYPES,
} from '@constants/documentDetail';
import { useSlideThumbnails } from '@hooks/useSlideThumbnails';
import type { DocumentDetailModel } from '@hooks/useDocumentDetail';
import type { PreviewError, PreviewState } from '@hooks/useDocumentPreview';

export interface DocumentPreviewPanelProps {
  doc: DocumentDetailModel;
  xlsxAnchor?: XlsxAnchor;
  previewUrl: string | null;
  previewLoading: boolean;
  previewError: PreviewError | null;
  onRetry: () => void;
  onUploadNewVersion: () => void;
  highlightTerms: string[];
  initialPage: number;
  initialPageExplicit: boolean;
  preferredSearchPage?: number;
  /** Non-binary preview outcome, e.g. a deck still being converted. */
  previewState?: PreviewState | null;
  /** What the server actually returned; decides the viewer, not the file extension. */
  previewContentType?: string | null;
  /** Slides in the converted deck; 0 for anything that is not a presentation. */
  slideCount?: number;
}

const DocumentPreviewPanel: React.FC<DocumentPreviewPanelProps> = ({
  doc,
  xlsxAnchor,
  previewUrl,
  previewLoading,
  previewError,
  onRetry,
  onUploadNewVersion,
  highlightTerms,
  initialPage,
  initialPageExplicit,
  preferredSearchPage,
  previewState = null,
  previewContentType = null,
  slideCount = 0,
}) => {
  const { t } = useTranslation();
  const [showCells, setShowCells] = useState(false);
  useEffect(() => setShowCells(Boolean(xlsxAnchor)), [doc?.id, xlsxAnchor]);
  const fileType = doc?.fileType;
  const isSpreadsheetPreview =
    (previewContentType || '').split(';')[0].trim() === 'text/html' ||
    PREVIEWABLE_SPREADSHEET_TYPES.includes(fileType);
  const [selectedSlide, setSelectedSlide] = useState<number | null>(null);

  // The URL owns which page is shown; without this reset a thumbnail click pinned
  // initialPage, so a later citation or Back changed the URL and nothing else.
  useEffect(() => setSelectedSlide(null), [initialPage, doc?.id]);
  const thumbnails = useSlideThumbnails(doc?.id, slideCount);
  const isPdf = (previewContentType || '').split(';')[0].trim() === 'application/pdf';

  return (
    <div>
      {previewLoading && (
        <div className="flex h-[400px] flex-col items-center justify-center rounded-lg bg-muted">
          <Spinner size="lg" label="" />
          <p className="revy-cjk mt-4 text-sm leading-[1.5] text-muted-foreground">
            {t('documents.loadingPreview', 'Loading preview...')}
          </p>
        </div>
      )}

      {previewError && !previewLoading && (
        <div className="mb-4">
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="revy-cjk leading-[1.6]">
              {previewError.message}
            </AlertDescription>
          </Alert>
          <div className="flex gap-4">
            {!isFileNotFoundError(previewError) && previewError.status !== 422 && (
              <Button variant="outline" onClick={onRetry}>
                {t('common.retry', 'Retry')}
              </Button>
            )}
            {(isFileNotFoundError(previewError) || previewError.status === 422) && (
              <Button onClick={onUploadNewVersion}>{t('documents.uploadNewVersion')}</Button>
            )}
          </div>
        </div>
      )}

      {!previewLoading && !previewError && !previewUrl && (previewState?.type === 'preview_pending' || previewState?.type === 'preview_converting') && (
        <div className="flex h-[400px] flex-col items-center justify-center rounded-lg bg-muted">
          <Spinner size="lg" label="" />
          <p className="revy-cjk mt-4 text-sm leading-[1.5] text-muted-foreground">
            {previewState?.type === 'preview_converting'
              ? t('documents.convertingSlidePreview')
              : t('documents.preparingSlidePreview', 'Preparing the slide preview...')}
          </p>
        </div>
      )}

      {!previewLoading && !previewError && !previewUrl && previewState?.type === 'preview_failed' && (
        <Alert variant="destructive">
          <AlertDescription className="revy-cjk leading-[1.6]">
            {previewState.reason
              ? t(`documents.slidePreviewFailedReason.${previewState.reason}`, {
                defaultValue: t('documents.slidePreviewFailed'),
              })
              : t('documents.slidePreviewFailed')}
          </AlertDescription>
        </Alert>
      )}

      {!previewLoading && !previewError && !previewUrl && previewState
        && previewState.type !== 'preview_pending'
        && previewState.type !== 'preview_converting'
        && previewState.type !== 'preview_failed' && (
        <div className="flex h-[400px] items-center justify-center rounded-lg border bg-muted">
          <p className="revy-cjk text-base leading-[1.5] text-muted-foreground">
            {t('documents.previewUnavailableForType', {
              type: fileType || t('documents.fileTypeFallback'),
            })}
          </p>
        </div>
      )}

      {!previewLoading && !previewError && previewUrl && (
        <>
          {isPdf || fileType === 'pdf' ? (
            <div className="flex gap-4">
              {thumbnails.length > 0 && (
                <ol className="flex h-[650px] w-32 shrink-0 flex-col gap-2 overflow-y-auto">
                  {thumbnails.map((thumbnail, index) => (
                    <li key={`slide-${index + 1}`}>
                      <button
                        type="button"
                        onClick={() => setSelectedSlide(index + 1)}
                        aria-current={selectedSlide === index + 1}
                        className="w-full rounded-sm border p-1 aria-[current=true]:border-primary"
                      >
                        {thumbnail ? (
                          <img
                            src={thumbnail}
                            alt={t('documents.slideNumber', { number: index + 1 })}
                            className="w-full object-contain"
                          />
                        ) : (
                          // A thumbnail that failed stays a labelled placeholder: the
                          // slide is still there and still navigable.
                          <span className="flex h-16 w-full items-center justify-center bg-muted text-xs text-muted-foreground">
                            {t('documents.slideNumber', { number: index + 1 })}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ol>
              )}
              <div className="relative h-[650px] w-full rounded-sm border">
                <PDFHighlightViewer
                  pdfUrl={previewUrl}
                  highlightTerms={highlightTerms}
                  initialPage={selectedSlide ?? initialPage}
                  initialPageExplicit={selectedSlide !== null || initialPageExplicit}
                  preferredSearchPage={preferredSearchPage}
                />
              </div>
            </div>
          ) : PREVIEWABLE_IMAGE_TYPES.includes(fileType) ? (
            <div className="text-center">
              <img
                src={previewUrl}
                alt={doc.title}
                className="mx-auto max-h-[600px] max-w-full object-contain"
              />
            </div>
          ) : isSpreadsheetPreview ? (
            <div>
              <div className="mb-3 flex gap-2"><Button variant="outline" onClick={() => setShowCells(v => !v)}>{t('documents.cells.toggle')}</Button></div>
              {showCells ? <XlsxCellViewer key={`${doc.id}:${JSON.stringify(xlsxAnchor)}`} documentId={doc.id} anchor={xlsxAnchor} /> : <iframe
              src={previewUrl}
              title={t('documents.spreadsheetPreviewFrameTitle')}
              sandbox=""
              referrerPolicy="no-referrer"
              className="h-[650px] w-full rounded-sm border bg-background"
            />}
            </div>
          ) : (
            <div className="flex h-[400px] items-center justify-center rounded-lg border bg-muted">
              <p className="revy-cjk text-base leading-[1.5] text-muted-foreground">
                {t('documents.previewUnavailableForType', {
                  type: fileType || t('documents.fileTypeFallback'),
                })}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentPreviewPanel;
